import {
  findSectionModal,
  openSectionModal,
  sectionModalBody,
} from '@/modules/public/room/dependencies/roompage.section-modal';

export const EXPERIMENT_ID = 'tm-8339-room-page-compact-v2';

const VERSION_COMPACT = 1;

const MAIN_COLUMN = '#col-main';

/**
 * Facilities preview on the page; the rest of the list lives in the modal. The list renders what
 * the space has ahead of what it lacks, so a space with fewer makes up the count with the latter.
 */
const FACILITIES_PREVIEW_COUNT = 4;
/** Catering keeps its first heading plus this many sub-items. */
const CATERING_PREVIEW_COUNT = 4;
/** A catering heading with nothing under it borrows this many of the headings after it. */
const CATERING_FALLBACK_COUNT = 2;
/** Rules keeps the allowed events, capped so the block stays a preview. */
const RULES_PREVIEW_COUNT = 4;

let version = null;
let impressionSent = false;

const isCompact = () => version === VERSION_COMPACT;

/** Not a direct child lookup: for some search tags sortRoomPageSections() nests facilities inside catering. */
const findSection = (container, name) => container.querySelector(`[data-room-section="${name}"]`);

const separatorBefore = (section) => {
  const previous = section.previousElementSibling;

  return previous?.classList.contains('separator-room') ? previous : null;
};

/**
 * sortRoomPageSections() hides, with an inline style, whatever the visitor's search tag makes
 * irrelevant. Both versions run it, so the compacted one has to leave those out too.
 */
const isHiddenByTagSort = (element) => element.closest('[style*="display: none"]') !== null;

/** Bumped for every svg id a copy carries, so no clone ever shares one with the page. */
let copiedSvgIds = 0;

const stripIds = (root) => {
  root.removeAttribute('id');

  root.querySelectorAll('svg [id]').forEach((defined) => {
    const svg = defined.closest('svg');
    const reference = `url(#${defined.id})`;
    const unique = `${defined.id}-copy-${(copiedSvgIds += 1)}`;

    svg.querySelectorAll('*').forEach((el) => {
      Array.from(el.attributes)
        .filter((attribute) => attribute.value.includes(reference))
        .forEach((attribute) => {
          attribute.value = attribute.value.replace(reference, `url(#${unique})`);
        });
    });

    defined.id = unique;
  });

  root.querySelectorAll('[id]').forEach((el) => {
    if (!el.closest('svg')) {
      el.removeAttribute('id');
    }
  });
};

/** Point 4 of the ticket: hover tooltips give way to the always-visible descriptions. */
const stripTooltips = (root) => {
  root.querySelectorAll('[role="tooltip"]').forEach((el) => {
    el.removeAttribute('role');
    el.removeAttribute('aria-label');
  });
};

/** The modal shows the complete list, so whatever the page truncated is restored inside it. */
const untruncate = (root) => {
  root.querySelectorAll('.hidden').forEach((el) => el.classList.remove('hidden'));
  root.querySelectorAll('.btn-truncate-more, .btn-truncate-less').forEach((el) => el.remove());
};

/** Bootstrap grid classes from the page; the modal lays the same content out in one column. */
const GRID_CLASS_PATTERN = /^row$|^col-/;

const stripGrid = (root) => {
  [root, ...root.querySelectorAll('[class]')].forEach((el) => {
    const gridClasses = Array.from(el.classList).filter((name) => GRID_CLASS_PATTERN.test(name));

    if (gridClasses.length === 0) {
      return;
    }

    el.classList.remove(...gridClasses);

    // .room-features stays a flex row, so a column that loses its bootstrap width would shrink
    // to its content and share a line with the next one instead of stacking.
    if (gridClasses.some((name) => name.startsWith('col-'))) {
      el.classList.add('w-full');
    }
  });
};

const FEATURE_ROW_SELECTOR = '.room__feature, .c-room-feature';
const DIVIDER_CLASSES = ['border-0', 'border-b', 'border-solid', 'border-grey-300'];

/**
 * Rebuilds the page's feature rows to the modal's proportions. Catering is the odd one out: it
 * nests sub-items under a bold parent and divides whole categories rather than single rows, so
 * it opts out of the per-row rule the facilities and rules modals use.
 */
const styleFeatureRows = (root) => {
  const categories = Array.from(root.querySelectorAll('.catering-category'));
  const subItems = new Set(root.querySelectorAll('.c-room-feature-list .c-room-feature'));
  const rows = Array.from(root.querySelectorAll(FEATURE_ROW_SELECTOR));
  const lastRow = rows[rows.length - 1];

  // On the page the list is a two-column flex row; in the modal every item takes a full line.
  // Its first sub-item leads with 8px of its own, so the list only makes up the rest of the gap -
  // and a category the venue said no to still renders the list, empty, which must not take space.
  root.querySelectorAll('.c-room-feature-list').forEach((list) => {
    list.classList.remove('c-room-feature-list');

    if (list.children.length > 0) {
      list.classList.add('mt-2');
    }
  });

  rows.forEach((row) => {
    row.classList.add('flex-wrap', 'items-start', 'w-full', 'mb-0', 'leading-5');

    // .c-room-feature pads 8px top and bottom; leading with the whole gap keeps the rhythm
    // between sub-items but lets a category close flush against the rule below it.
    if (subItems.has(row)) {
      row.classList.add('pt-2', 'pb-0');

      return;
    }

    if (row.classList.contains('c-room-feature--bold')) {
      row.classList.add('py-0', 'text-base', 'leading-6');

      return;
    }

    row.classList.add(categories.length > 0 ? 'py-0' : 'py-4');

    // A group closes on its last row; the design draws no rule underneath it.
    if (categories.length === 0 && row !== lastRow) {
      row.classList.add(...DIVIDER_CLASSES);
    }
  });

  // The page pads a category with 16px of its own, which would leave the rule off-centre.
  // Dropping the class puts both sides of it on the 24px the design asks for.
  categories.forEach((category) => category.classList.remove('catering-category'));
  categories.slice(0, -1).forEach((category) => {
    category.classList.add('pb-6', 'mb-6', ...DIVIDER_CLASSES);
  });
};

/** The design draws these at 20px; the page's own rows use 16px, and the svg scales to its box. */
const resizeIcons = (root) => {
  root.querySelectorAll('.c-icon').forEach((icon) => {
    icon.style.marginRight = '8px';

    if (icon.classList.contains('c-icon--fixed-width')) {
      icon.style.width = '20px';
      icon.style.height = '20px';
    }
  });
};

/**
 * The page strikes an unavailable row through as a whole. Only its label should carry the rule -
 * anything else the row picks up later, a description among them, stays readable.
 */
const restyleUnavailableRows = (root) => {
  root.querySelectorAll('.not-colored-strikethrough, .d-room-feature--inactive').forEach((row) => {
    row.classList.remove('not-colored-strikethrough', 'd-room-feature--inactive');
    row.querySelectorAll(':scope > span:not(.c-feature-description)')
      .forEach((label) => label.classList.add('line-through'));
  });
};

/** Descriptions ship hidden so they stay out of the page itself - the modal is where they show. */
const revealDescriptions = (root) => {
  root.querySelectorAll('.c-feature-description').forEach((description) => {
    description.classList.remove('none');
    description.classList.add('block');
  });
};

const modalCopyOf = (element) => {
  const copy = element.cloneNode(true);

  stripIds(copy);
  stripTooltips(copy);
  untruncate(copy);
  stripGrid(copy);
  styleFeatureRows(copy);
  resizeIcons(copy);
  restyleUnavailableRows(copy);
  revealDescriptions(copy);

  return copy;
};

const groupTitle = (text) => {
  const heading = document.createElement('h3');

  heading.className = 'mt-8 mb-2 first:mt-0 text-base font-semibold leading-6';
  heading.textContent = text;

  return heading;
};

const showAllButton = (label, onClick) => {
  const button = document.createElement('button');

  button.type = 'button';
  button.className = 'ds-button ds-button--white js-room-show-all';
  button.textContent = label;
  button.addEventListener('click', onClick);

  return button;
};

const track = (section, element) => {
  App.track.event('room_page_interaction', {
    section,
    element,
    interaction: 'click',
  });
};

/**
 * Sent on page load, for the control version as well as the compacted one: the two differ from
 * the moment the page renders, so every allocated visitor has already been exposed. Waiting for
 * an interaction would under-count the experiment.
 */
const trackImpression = () => {
  if (impressionSent || version === null) {
    return;
  }

  impressionSent = true;

  App.track.event('experiment_impression', {
    experiment_id: EXPERIMENT_ID,
    variant_id: version,
  });
};

const contentColumnOf = (section) => section.querySelector('[class*="col-sm-9"], [class*="col-md-8"]');

const removeSection = (section) => {
  separatorBefore(section)?.remove();
  section.remove();
};

/**
 * Facilities, music & sound equipment and accessibility become one modal. Facilities keeps a
 * short preview on the page; the other two are only reachable through the modal.
 */
const buildFacilitiesModal = (container) => {
  const facilities = findSection(container, 'facilities');
  const modal = findSectionModal('facilities');
  const body = sectionModalBody(modal);

  if (!facilities || !body) {
    return;
  }

  const content = contentColumnOf(facilities);
  const list = facilities.querySelector('.room-features');

  if (!content || !list) {
    return;
  }

  const groups = [{ title: 'Space amenities', copy: modalCopyOf(list) }];

  [
    { name: 'music-and-sound', title: 'Music & sound equipment' },
    { name: 'accessibility', title: 'Accessibility' },
  ].forEach(({ name, title }) => {
    const section = findSection(container, name);
    const sectionContent = section && contentColumnOf(section);

    if (!sectionContent) {
      return;
    }

    if (!isHiddenByTagSort(section)) {
      groups.push({ title, copy: modalCopyOf(sectionContent) });
    }

    removeSection(section);
  });

  // Counted while the copies are still detached, so only what the tag sort hid inside them is skipped.
  const total = groups.flatMap(({ copy }) => Array.from(copy.querySelectorAll(FEATURE_ROW_SELECTOR)))
    .filter((row) => !isHiddenByTagSort(row))
    .length;

  groups.forEach(({ title, copy }) => body.append(groupTitle(title), copy));

  stripTooltips(facilities);

  // The page's own truncation - and the "Show more" it appended next to the list - gives way
  // to a fixed-size preview.
  untruncate(content);

  let shown = 0;
  Array.from(list.children).forEach((item) => {
    if (isHiddenByTagSort(item)) {
      return;
    }

    if (shown < FACILITIES_PREVIEW_COUNT) {
      shown += 1;

      return;
    }

    item.classList.add('none');
  });

  content.append(showAllButton(`Show all ${total} facilities`, () => {
    track('facilities', 'show_all_facilities');
    openSectionModal(modal);
  }));
};

/**
 * Catering keeps its own section but only its first heading and a handful of sub-items; the
 * full breakdown moves into a modal.
 */
const buildCateringModal = (container) => {
  // Catering's own row, so a facilities section the tag sort moved in ahead of it is left alone.
  const catering = findSection(container, 'catering')?.querySelector('#roomCateringSection');
  const modal = findSectionModal('catering');
  const body = sectionModalBody(modal);
  const content = catering && contentColumnOf(catering);

  if (!content || !body) {
    return;
  }

  body.append(modalCopyOf(content));

  stripTooltips(catering);

  // Categories are ordered by type, so the first one is as likely to say what the venue can't
  // do as what it can. The preview leads with what the space actually offers, and only falls
  // back to a negative when there is nothing positive to show - the rest stays in the modal.
  const categories = Array.from(content.querySelectorAll('.catering-category'));
  const preview = categories.find((category) => category.dataset.available === 'true') ?? categories[0];

  categories
    .filter((category) => category !== preview)
    .forEach((category) => category.classList.add('none'));

  const subItems = Array.from(preview?.querySelectorAll('.c-room-feature-list .c-room-feature') ?? []);
  subItems
    .slice(CATERING_PREVIEW_COUNT)
    .forEach((item) => item.classList.add('none'));

  // A heading on its own ("In-house catering available") tells the Client nothing, so the
  // headings that follow it in the modal fill in - without their sub-items, to stay a preview.
  if (preview && subItems.length === 0) {
    const previewIndex = categories.indexOf(preview);

    categories
      .slice(previewIndex + 1, previewIndex + 1 + CATERING_FALLBACK_COUNT)
      .forEach((category) => {
        category.classList.remove('none');
        category.querySelector('.c-room-feature-list')?.classList.add('none');
      });
  }

  content.append(showAllButton('Show all catering', () => {
    track('catering', 'show_all_catering');
    openSectionModal(modal);
  }));
};

/**
 * Rules of the space keeps the allowed events; the age policy, host rules and cancellation
 * terms move into a modal.
 */
const buildRulesModal = (container) => {
  const rules = findSection(container, 'rules');
  const modal = findSectionModal('rules');
  const body = sectionModalBody(modal);
  const content = rules && contentColumnOf(rules);
  const allowedEvents = content?.querySelector('#allowedEvents');

  // The allowed events are all the preview keeps. When the visitor's search tag hid them there is
  // nothing left to preview, so the section stays as the control shows it.
  if (!allowedEvents || isHiddenByTagSort(allowedEvents) || !body) {
    return;
  }

  const fullCopy = modalCopyOf(content);
  // The page's own "Show cancellation details" toggle has no handler inside the modal.
  fullCopy.querySelector('.js-show-all-cancellation-policies')?.remove();
  fullCopy.querySelector('.cancellation-policies-block')?.removeAttribute('style');

  // The page spaces these blocks 16px and 32px apart; the modal draws the design's 24px between
  // them. The first heading opens the modal body, so it keeps the flush top it already has.
  Array.from(fullCopy.querySelectorAll('h3')).slice(1).forEach((heading) => {
    heading.classList.remove('mt-4', 'mt-8');
    heading.classList.add('mt-6');
  });

  body.append(fullCopy);

  stripTooltips(rules);
  untruncate(content);

  Array.from(content.children)
    .filter((element) => element !== allowedEvents)
    .forEach((element) => element.classList.add('none'));

  Array.from(allowedEvents.querySelector('.js-truncate')?.children ?? [])
    .slice(RULES_PREVIEW_COUNT)
    .forEach((item) => item.classList.add('none'));

  content.append(showAllButton('Show all rules', () => {
    track('rules', 'show_all_rules');
    openSectionModal(modal);
  }));
};

/**
 * Allocates the visitor and, for the compacted variant, hides the sections that only live in the
 * facilities modal. Runs before first paint so they never visibly disappear. Both versions keep
 * the page's own section order.
 *
 * Both versions are server-rendered because room pages are edge-cached - one HTML response
 * reaches everyone, so the split can only happen in the browser. That also keeps the full
 * facilities and catering copy in the initial HTML, which is what SEO asked for.
 *
 * @todo TM-8339 keep the winning layout and drop the other one once the experiment is settled
 * @returns {?number} the allocated version, or null for visitors outside the experiment
 */
export const applyRoomPageCompactExperiment = () => {
  // Only the room page has this column; the module is bundled with pages that don't.
  const container = document.querySelector(MAIN_COLUMN);

  if (!container) {
    return null;
  }

  version = window.getVersionFromCookieId?.(EXPERIMENT_ID) ?? null;

  if (!isCompact()) {
    return version;
  }

  // Hidden now, moved into the facilities modal once the page's truncation has run.
  ['music-and-sound', 'accessibility'].forEach((name) => {
    const section = findSection(container, name);
    if (section) {
      section.classList.add('none');
    }
  });

  return version;
};

/**
 * Fills the modals and trims the previews. Must run after App.views.room.truncate(), which is
 * what adds the page's own "Show more" toggles.
 */
export const applyRoomPageModals = () => {
  if (!isCompact()) {
    return;
  }

  const container = document.querySelector(MAIN_COLUMN);
  if (!container) {
    return;
  }

  buildFacilitiesModal(container);
  buildCateringModal(container);
  buildRulesModal(container);
};

/**
 * Called once the page is ready, because App.track is set up by a separate bundle and is not
 * guaranteed to exist while this module is still being evaluated.
 */
export const trackRoomPageCompactImpression = () => trackImpression();
